import { RootState } from '../redux/store'
import { useSelector } from 'react-redux'
import * as queryString from 'query-string'
import { balanceOfdebtType } from '../constants/balanceOfdebtConstants'
import moment from 'moment'
import { notificationService } from '../services/notificationService'
import {
  currencyPrefKey,
  getNumberDisplayDecimals,
  getNumberDisplayLocale,
  type NumberDecimalPrefKey,
} from './numberDisplayFormatPrefs'

export type MessageType = 'success' | 'error' | 'info' | 'warning'

export function showNoti(
  type: MessageType,
  text: string,
  options?: { duration?: number; preventDuplicate?: boolean },
) {
  try {
    notificationService.show(text, type, options)
  } catch {
    console[type === 'error' ? 'error' : 'info'](text)
  }
}

export function showMessage(
  type: MessageType | 'loading',
  text: string,
  options?: { duration?: number; preventDuplicate?: boolean },
) {
  const severity: MessageType = type === 'loading' ? 'info' : type
  try {
    notificationService.show(text, severity, {
      duration: type === 'loading' ? 4500 : options?.duration,
      preventDuplicate: options?.preventDuplicate,
    })
  } catch {
    console[severity === 'error' ? 'error' : 'info'](text)
  }
}

export function isEmailValid(email: string) {
  let re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

export function isPhoneValid(phone: string) {
  try {
    const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance()
    const phoneNumber = phoneUtil.parse(phone, 'VN')
    return phoneUtil.isValidNumber(phoneNumber, 'VN')
  } catch (e) {
    return false
  }
}

export function handleError(err: any) {
  if (err.message) showMessage('error', err.message + ' (' + err.code + ')')
  else {
    console.error(err)
  }
}

export function getBase64(file: any) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}

/** Định dạng theo cài đặt trình duyệt (dấu phân cách + số chữ số thập phân tối đa). */
function formatNumberByPrefs(value: number | undefined, key: NumberDecimalPrefKey) {
  return Number(value).toLocaleString(getNumberDisplayLocale(), {
    maximumFractionDigits: getNumberDisplayDecimals(key),
    useGrouping: true,
  })
}

export function showPrice(price: number | undefined) {
  if (price == undefined) return ""
  return formatNumberByPrefs(price, 'general')
}

/** Số tiền theo loại tiền (`RMB` / `USD` / `VND`); loại lạ → "Số chung". */
export function showPriceByCurrency(price: number | undefined | null, currency?: string | null) {
  if (price == undefined) return ""
  return formatNumberByPrefs(price, currencyPrefKey(currency))
}

export function showPriceVnd(price: number | undefined | null) {
  return showPriceByCurrency(price, 'VND')
}

/**
 * Hiển thị field số của record, tự nhận loại tiền:
 * key đuôi `VND` → tiền Việt; có field `${key}LoaiTienTe` → theo loại tiền đó; còn lại → "Số chung".
 */
export function showPriceField(record: any, key: string) {
  const value = record?.[key]
  if (value == undefined) return ""
  if (/VND$/i.test(key)) return formatNumberByPrefs(value, 'vnd')
  const currency = record?.[`${key}LoaiTienTe`]
  return formatNumberByPrefs(value, currency ? currencyPrefKey(currency) : 'general')
}

/** Giá trị ô Excel: số thật (định dạng do `writeExcelBuffer` gán numFmt theo cài đặt). */
export function numberExcel(price: number | string | undefined | null): number | '' {
  if (price == undefined || price === '') return ''
  const n = Number(price)
  return Number.isFinite(n) ? n : ''
}

export function convertGender(gender: number) {
  if (gender === 1) return 'Nam'
  if (gender === 2) return 'Nữ'
  return ''
}

export const renderFormula = (formula: string) => {
  switch (formula) {
    case 'compare_kg_and_m3':
      return 'So sánh giá'
    case 'per_kg':
      return 'Theo cân nặng (kg)'
    case 'per_m3':
      return 'Theo thể tích (m3)'
    default:
      return ''
  }
}

export const renderExportImportService = (exportImportService: string) => {
  switch (exportImportService) {
    case 'container_consolidation':
      return 'Gom container'
    case 'full_container':
      return 'Full container'
    default:
      return 'Chưa cấu hình'
  }
}

export const handleChangePaging = ({
  history,
  pathname,
  search,
  page,
  pageSize,
}: {
  history: any
  pathname: string
  search: string
  page: number
  pageSize?: number
}) => {
  const parsed: any = queryString.parse(search)
  parsed.page = page
  if (pageSize) parsed.page_size = pageSize
  const stringified = queryString.stringify(parsed, { skipEmptyString: true })
  history.push(pathname + '?' + stringified)
}


export function formatter(value: number | undefined) {
  return formatNumberByPrefs(value, 'general')
}

export function formatterSoKhoi(value: number | undefined) {
  return formatNumberByPrefs(value, 'volume')
}

export const formatterNumber = (val) => {
  if (!val) return 0;
  return `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(?=\d{0,2}$)/g, ",");
}


export const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const openInTab = (url: string) => {
  window.open(url, "_self");
};

export const delay = async (value: number) => {
  await new Promise(resolve => setTimeout(resolve, value));
};

// Hàm kiểm tra quyền Clipboard
export async function requestClipboardPermission(): Promise<boolean> {
  try {
    const permissionStatus = await navigator.permissions.query({ name: "clipboard-write" as any });

    if (permissionStatus.state === "granted" || permissionStatus.state === "prompt") {
      console.log("Quyền clipboard đã được cấp hoặc có thể yêu cầu.");
      return true;
    } else {
      console.error("Quyền clipboard bị từ chối.");
      return false;
    }
  } catch (error) {
    console.error("Không thể kiểm tra quyền clipboard:", error);
    return false;
  }
}

// Hàm sao chép văn bản
export async function copyToClipboardWithPermission(text: string): Promise<void> {
  const hasPermission = await requestClipboardPermission();
  if (hasPermission) {
    try {
      await navigator.clipboard.writeText(text);
      console.log("Văn bản đã được sao chép thành công!");
    } catch (error) {
      console.error("Không thể sao chép văn bản:", error);
    }
  } else {
    console.error("Không có quyền truy cập Clipboard.");
  }
}

// Gọi hàm sao chép
export async function handleCopy() {
  await copyToClipboardWithPermission("Nội dung cần sao chép");
}


export const getTransProviderById = (nccVanChuyenId: number) => {
  const transProviderList = useSelector((state: RootState) => state.transProvider.transProviderList || {})
  let nccVanChuyenTen = nccVanChuyenId
  if (Array.isArray(transProviderList.data) && transProviderList.data.length > 0) {
    transProviderList.data.map((item: any) => {
      if (item.id) {
        nccVanChuyenTen = item.ten
      }
    })
  }
  return nccVanChuyenTen
}

export const convertLoaiDoiTuong = (loaiDoiTuong: string) => {
  switch (loaiDoiTuong) {
    case balanceOfdebtType.NCC_THUCTE:
      return "CN NCC thực tế"
    case balanceOfdebtType.NCC_NGAYPHATHANGTHUCTE:
      return "CN NCC theo ngày phát hàng thực tế"
    case balanceOfdebtType.NCC_NGAYDATHANGTHUCTE:
      return "CN NCC theo ngày đặt hàng thực tế"
    case balanceOfdebtType.NCCVC:
      return "Công nợ vận chuyển"
    case balanceOfdebtType.NCCTIENTE:
      return "Công nợ thanh toán"
    default:
      return ""
  }
}


export const isHaveValue = (value: any) => {
  return value != null && value != "" && value != undefined
}

export const fomartDateTime = (date: string | Date) => {
  return date ? moment(date).format('DD/MM/YYYY | HH:mm') : ''
}

const pad2Datetime = (n: number) => String(n).padStart(2, '0')

/** Chuỗi local cho ô ngày giờ: `YYYY-MM-DDTHH:mm` (24h). */
export const toLocalDatetimeInputValue = (d: Date): string =>
  `${d.getFullYear()}-${pad2Datetime(d.getMonth() + 1)}-${pad2Datetime(d.getDate())}T${pad2Datetime(
    d.getHours(),
  )}:${pad2Datetime(d.getMinutes())}`

/** Chuẩn hoá chuỗi giờ về `HH:mm` (24h). Không hợp lệ → `''`. */
export const normalizeTime24h = (raw: string): string => {
  const t = raw.trim()
  const m = t.match(/^(\d{1,2}):(\d{2})$/)
  if (m) {
    const h = Number(m[1])
    const min = Number(m[2])
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
      return `${pad2Datetime(h)}:${pad2Datetime(min)}`
    }
    return ''
  }
  const digits = t.replace(/\D/g, '')
  if (digits.length === 3 || digits.length === 4) {
    const h = Number(digits.length === 3 ? digits.slice(0, 1) : digits.slice(0, 2))
    const min = Number(digits.slice(-2))
    if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
      return `${pad2Datetime(h)}:${pad2Datetime(min)}`
    }
  }
  return ''
}


